import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import AgoraRTC from "agora-rtc-sdk-ng";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import deviceProfile from "images/device.png";
import { KeyboardVoiceRounded, MicOffRounded, VideocamRounded, VideocamOffRounded, } from "@mui/icons-material";
import PhoneCallIcon from "@mui/icons-material/PhoneRounded";
import EditIcon from "@mui/icons-material/EditRounded";
import TrashIcon from "@mui/icons-material/DeleteRounded";
import PairingTab from "./Tabs/pairing";
import DoNotDisturb from "./Tabs/dnd";
import DisplaySettings from "./Tabs/display_settings";
import CarerMode from "./Tabs/carer_settings";
import SettingsTab from "./Tabs/settings";
import ClockSettingsTab from "./Tabs/clockSettings";
import WorkerCheckInTab from "./Tabs/worker_checkin";
import RecordingSettingsTab from "./Tabs/recordSettings";
import PaginatedDeviceSelectionTable from "./PaginatedDeviceSelectionTable";
import {
    fetchPolicyGroups, fetchPolicyDevices, createPolicyGroup, updatePolicyGroup, deletePolicyGroup, addDeviceToPolicy, removeDeviceFromPolicy, fetchOrganizationDevices,
} from "js/Api/index";
import { Button } from '../../Component/CommonFormEle';

function PolicyGroups({ user, userInfo, onApplyToAllDevices, onApplyToThisDevice, settingsFrom, org, resetFlag, }) {
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [selectedTab, setSelectedTab] = useState("Devices in Group");
    const [selectedPolicyTab, setSelectedPolicyTab] = useState("Call Settings");
    const [policyGroups, setPolicyGroups] = useState([]);
    const [showAddPolicyGroupModal, setShowAddPolicyGroupModal] = useState(false);
    const [newPolicyGroup, setNewPolicyGroup] = useState({ name: "" });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteGroupId, setDeleteGroupId] = useState(null);
    const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
    const [devices, setDevices] = useState([]);
    const [selectedDevices, setSelectedDevices] = useState([]);
    const [policyDevices, setPolicyDevices] = useState([]);
    const [showEditPolicyGroupModal, setShowEditPolicyGroupModal] =
        useState(false);
    const [editedPolicyGroup, setEditedPolicyGroup] = useState({
        id: "",
        name: "",
        status: "",
        noOfDevices: 0,
    });
    const [searchQuery, setSearchQuery] = useState("");
    const [policyGroupSearchQuery, setPolicyGroupSearchQuery] = useState("");
    const [policyDeviceSearchQuery, setPolicyDeviceSearchQuery] = useState("");

    //group call
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewVideoTrack, setPreviewVideoTrack] = useState(null);
    const [previewAudioTrack, setPreviewAudioTrack] = useState(null);
    const [isPreviewCameraEnabled, setIsPreviewCameraEnabled] = useState(true);
    const [isPreviewMicrophoneEnabled, setIsPreviewMicrophoneEnabled] =
        useState(true);
    const previewVideoRef = useRef(null);

    const [audioInputDevices, setAudioInputDevices] = useState([]);
    const [audioOutputDevices, setAudioOutputDevices] = useState([]);
    const [videoDevices, setVideoDevices] = useState([]);
    const [selectedAudioInput, setSelectedAudioInput] = useState("");
    const [selectedAudioOutput, setSelectedAudioOutput] = useState("");
    const [selectedCamera, setSelectedCamera] = useState("");
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        fetchPolicyGroupsData();
    }, [org.id]);

    useEffect(() => {
        setSelectedGroup(null);
    }, [resetFlag]);

    useEffect(() => {
        if (selectedGroup) {
            fetchPolicyDevicesData();
        }
    }, [selectedGroup]);

    const fetchPolicyGroupsData = async () => {
        try {
            const response = await fetchPolicyGroups(org.id);
            if (response.success) {
                setPolicyGroups(response.data);

                let hashes = location.hash.split("#");
                if (hashes.length > 2 && hashes[2].includes("id=")) {
                    const sel_id = hashes[2].replace("id=", "");
                    const item = response.data.find(a => a.id === sel_id);
                    if (item !== null) {
                        setSelectedGroup(item);
                    }
                }
            } else {
                console.error("Failed to fetch policy groups:", response.error);
            }
        } catch (error) {
            console.error("Error fetching policy groups:", error);
        }
    };

    const fetchOrgDevicesData = async () => {
        try {
            const response = await fetchOrganizationDevices(org.code, false);
            if (response.success) {
                setDevices(response.data);
            } else {
                console.error("Failed to fetch available devices:", response.error);
            }
        } catch (error) {
            console.error("Error fetching available devices:", error);
        }
    };

    const fetchPolicyDevicesData = async () => {
        if (!selectedGroup) return;

        try {
            const response = await fetchPolicyDevices(selectedGroup.id);
            if (response.success) {
                setPolicyDevices(response.data);
            } else {
                console.error("Failed to fetch policy devices:", response.error);
            }
        } catch (error) {
            console.error("Error fetching policy devices:", error);
        }
    };

    const handleGroupClick = (group) => {
        setSelectedGroup(group);
        if (group === null) {
            fetchPolicyGroups();
        }
    };

    const handleTabClick = (tab) => {
        setSelectedTab(tab);
    };

    const handlePolicyTabClick = (tab) => {
        setSelectedPolicyTab(tab);
    };

    const handleAddClick = () => {
        setShowAddPolicyGroupModal(true);
    };

    const handleInputChange = (e) => {
        setNewPolicyGroup({ ...newPolicyGroup, [e.target.name]: e.target.value });
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await createPolicyGroup({
                organizationId: org.id,
                name: newPolicyGroup.name,
            });

            if (response.success) {
                setNewPolicyGroup({ name: "" });
                setShowAddPolicyGroupModal(false);
                toast.success("Policy group created!");
                fetchPolicyGroupsData();
            } else {
                toast.error(response.error || "Failed to create policy group.");
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("An error occurred while creating the policy group.");
        }
    };

    const handleAddCancel = () => {
        setShowAddPolicyGroupModal(false);
    };

    const handleAddDeviceClick = () => {
        setSearchQuery("");
        setShowAddDeviceModal(true);
        fetchPolicyDevicesData();
        fetchOrgDevicesData();
    };

    const handleDeviceSelect = (deviceId) => {
        if (selectedDevices.includes(deviceId)) {
            setSelectedDevices(selectedDevices.filter((id) => id !== deviceId));
        } else {
            setSelectedDevices([...selectedDevices, deviceId]);
        }
    };

    const handleAddDevicesToGroup = async () => {
        try {
            let hasError = false;

            for (const deviceId of selectedDevices) {
                const response = await addDeviceToPolicy(deviceId, selectedGroup.id);

                if (!response.success) {
                    toast.error(
                        response.error || `Failed to add device to policy group.`
                    );
                    hasError = true;
                }
            }

            setSelectedDevices([]);
            setShowAddDeviceModal(false);

            if (!hasError) {
                toast.success("Devices added to policy group!");
            }

            fetchPolicyDevicesData();
        } catch (error) {
            console.error("Error:", error);
            toast.error(
                "An error occurred while adding devices to the policy group."
            );
        }
    };

    const handleDeleteGroupConfirm = async () => {
        try {
            const response = await deletePolicyGroup(deleteGroupId);

            if (response.success) {
                const updatedPolicyGroups = policyGroups.filter(
                    (group) => group.id !== deleteGroupId
                );
                setPolicyGroups(updatedPolicyGroups);
                setDeleteGroupId(null);
                toast.success("Policy group deleted!");
                setShowDeleteModal(false);
                fetchPolicyGroupsData();
            } else {
                toast.error(response.error || "Failed to delete policy group.");
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("An error occurred while deleting the policy group.");
        }
    };

    const handleDeleteGroupCancel = () => {
        setShowDeleteModal(false);
    };

    const handleRemoveClick = async (deviceId, policyId) => {
        try {
            const response = await removeDeviceFromPolicy(deviceId, policyId);

            if (response.success) {
                toast.success("Device removed successfully!");
                fetchPolicyDevicesData();
            } else {
                toast.error(response.error || "Failed to remove device.");
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("An error occurred while removing the device.");
        }
    };

    const handleEditClick = (group) => {
        setEditedPolicyGroup({
            id: group.id,
            name: group.name,
            status: group.status,
            noOfDevices: group.noOfDevices,
        });
        setShowEditPolicyGroupModal(true);
    };

    const handleEditInputChange = (e) => {
        setEditedPolicyGroup({
            ...editedPolicyGroup,
            [e.target.name]: e.target.value,
        });
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await updatePolicyGroup(editedPolicyGroup);

            if (response.success) {
                setShowEditPolicyGroupModal(false);
                toast.success("Policy group updated!");
                fetchPolicyGroupsData();
            } else {
                toast.error(response.error || "Failed to update policy group.");
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("An error occurred while updating the policy group.");
        }
    };

    const handleEditCancel = () => {
        setShowEditPolicyGroupModal(false);
    };

    const handleSearchInputChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const filteredDevices = devices.filter(
        (device) =>
            (device.policyId === null || device.policyId === "00000000-0000-0000-0000-000000000000") &&
            (
                (device.name &&
                    device.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (device.serialNo &&
                    device.serialNo.toLowerCase().includes(searchQuery.toLowerCase()))
            )
    );

    const handlePolicyDeviceSearchInputChange = (e) => {
        setPolicyDeviceSearchQuery(e.target.value);
    };

    const filteredPolicyGroups = policyGroups.filter((group) =>
        group.name.toLowerCase().includes(policyGroupSearchQuery.toLowerCase())
    );

    const filteredPolicyDevices = policyDevices.filter(
        (device) =>
            device.name
                .toLowerCase()
                .includes(policyDeviceSearchQuery.toLowerCase()) ||
            device.serialNo
                .toLowerCase()
                .includes(policyDeviceSearchQuery.toLowerCase())
    );

    const filterDevices = (devices) => {
        return devices
            .filter(
                (device, index, self) =>
                    index === self.findIndex((d) => d.groupId === device.groupId)
            )
            .map((device) => ({
                deviceId: device.deviceId,
                ...device,
                label: device.label
                    .replace(/default|communications/gi, "")
                    .replace(/-/g, "")
                    .trim(),
            }));
    };

    const handleGroupCallClick = async () => {
        if (!filteredPolicyDevices || filteredPolicyDevices.length === 0) {
            toast.error(
                "Please add devices to this policy group before starting a group call.",
                {
                    duration: 3000,
                    position: "top-center",
                    style: {
                        backgroundColor: "#F5F5F5",
                        color: "#333",
                    },
                }
            );
            return;
        }

        setShowPreviewModal(true);
        setIsPreviewModalOpen(true);
        setIsPreviewCameraEnabled(true);
        setIsPreviewMicrophoneEnabled(true);

        try {
            await navigator.mediaDevices.getUserMedia({ audio: true, video: true });

            const audioDevices = await AgoraRTC.getMicrophones();
            const audioOutput = await AgoraRTC.getPlaybackDevices();
            const cameraDevices = await AgoraRTC.getCameras();

            setAudioInputDevices(filterDevices(audioDevices));
            setAudioOutputDevices(filterDevices(audioOutput));
            setVideoDevices(filterDevices(cameraDevices));
            let audioTrack;
            let videoTrack;
            await cleanupPreviewTracks();

            if (selectedAudioInput != null) {
                audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
                    microphoneId: selectedAudioInput,
                });
            } else {
                audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
            }

            if (selectedCamera != null) {
                videoTrack = await AgoraRTC.createCameraVideoTrack({
                    encoderConfig: {
                        width: 640,
                        height: 360,
                        frameRate: 15,
                    },
                    cameraId: selectedCamera,
                });
            } else {
                videoTrack = await AgoraRTC.createCameraVideoTrack({
                    encoderConfig: {
                        width: 640,
                        height: 360,
                        frameRate: 15,
                    },
                });
            }

            setPreviewAudioTrack(audioTrack);
            setPreviewVideoTrack(videoTrack);
            if (previewVideoRef.current) {
                videoTrack.play(previewVideoRef.current);
            }
        } catch (error) {
            console.error("Failed to create preview tracks", error);
            setIsPreviewCameraEnabled(false);
            setIsPreviewMicrophoneEnabled(false);
        }
    };

    const cleanupPreviewTracks = async () => {
        if (previewVideoTrack) {
            previewVideoTrack.stop();
            previewVideoTrack.close();
            setPreviewVideoTrack(null);
        }
        if (previewAudioTrack) {
            previewAudioTrack.stop();
            previewAudioTrack.close();
            setPreviewAudioTrack(null);
        }
    };

    const handleStartGroupCall = async () => {
        setShowPreviewModal(false);
        cleanupPreviewTracks();

        const callData = {
            userInfo,
            policyId: selectedGroup.id,
            callerName: userInfo?.name || "Admin",
            initialCameraEnabled: isPreviewCameraEnabled,
            initialMicrophoneEnabled: isPreviewMicrophoneEnabled,
            initialAudio: selectedAudioInput,
            initialVideo: selectedCamera,
            initialAudioOutput: selectedAudioOutput,
        };

        const encodedCallData = btoa(JSON.stringify(callData));

        const videoCallTab = window.open(
            `/video-call?data=${encodedCallData}&type=group`,
            "_blank"
        );

        if (videoCallTab) {
            videoCallTab.focus();
        } else {
            toast.error(
                "Unable to open video call tab. Please check your popup blocker settings."
            );
        }
    };

    const handleDeviceChange = (event, setter) => {
        setter(event.target.value);
    };

    const togglePreviewCamera = async () => {
        if (previewVideoTrack) {
            await previewVideoTrack.setEnabled(!isPreviewCameraEnabled);
            setIsPreviewCameraEnabled(!isPreviewCameraEnabled);
        }
    };

    const togglePreviewMicrophone = async () => {
        if (previewAudioTrack) {
            await previewAudioTrack.setEnabled(!isPreviewMicrophoneEnabled);
            setIsPreviewMicrophoneEnabled(!isPreviewMicrophoneEnabled);
        }
    };

    const buttonStyle = (enabled) => ({
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "60px",
        height: "60px",
        margin: "0 10px",
        borderRadius: "50%",
        border: "none",
        cursor: "pointer",
        transition: "all 0.3s ease",
        backgroundColor: enabled ? "#1976D2" : "#1976D2",
        boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
        color: "white",
    });

    return (
        <div className="midde_cont">
            <div className="container-fluid" style={{ paddingLeft: "0", paddingRight: "0" }} >
                <div className="row column_title">
                    <div className="col-md-12">
                        <div className="page_title" style={{ display: "flex", alignItems: "center" }} >
                            {selectedGroup ? (
                                <ArrowBackRoundedIcon onClick={() => handleGroupClick(null)} sx={{ fontSize: "2rem", color: "black", marginRight: "10px", cursor: "pointer", }} />
                            ) : null}
                            <h2 style={{ flex: "1" }}>Device Groups</h2>
                        </div>
                    </div>
                </div>

                <div className="white_shd full margin_bottom_30">
                    <div className="full inner_elements">
                        <div className="row">
                            <div className="col-md-12">
                                {selectedGroup ? (
                                    <div className="padding_infor_info">
                                        <h3 style={{ marginLeft: "30px" }}>{selectedGroup.name}</h3>
                                        <div>
                                            <div className="tab_style1">
                                                <div className="tabbar padding_infor_info">
                                                    <nav>
                                                        <div className="nav nav-tabs" id="nav-tab" role="tablist" >
                                                            <div className={`nav-item nav-link ${selectedTab === "Devices in Group" ? "active" : ""}`}
                                                                onClick={() => handleTabClick("Devices in Group")} >
                                                                Devices in Group
                                                            </div>

                                                            <div className={`nav-item nav-link ${selectedTab === "Policy Settings" ? "active" : ""}`}
                                                                onClick={() => handleTabClick("Policy Settings")} >
                                                                Group Settings
                                                            </div>
                                                        </div>
                                                    </nav>
                                                </div>
                                            </div>

                                            <div className="tab-content">
                                                {selectedTab === "Policy Settings" && (
                                                    <div className="row">
                                                        <div className="col-md-3">
                                                            <nav>
                                                                <div className="nav flex-column nav-pills" id="nav-tab" role="tablist" style={{ paddingLeft: "25px" }} >
                                                                    <div className={`nav-item nav-link ${selectedPolicyTab === "Call Settings" ? "active" : ""}`}
                                                                        onClick={() => handlePolicyTabClick("Call Settings")} style={{ width: "140px" }} >
                                                                        Call Settings
                                                                    </div>
                                                                    <div className={`nav-item nav-link ${selectedPolicyTab === "Display" ? "active" : ""}`}
                                                                        onClick={() => handlePolicyTabClick("Display")} style={{ width: "140px" }} >
                                                                        Display
                                                                    </div>
                                                                    <div className={`nav-item nav-link ${selectedPolicyTab === "Do Not Disturb" ? "active" : ""}`} onClick={() => handlePolicyTabClick("Do Not Disturb")} style={{ width: "140px" }} >
                                                                        Do Not Disturb
                                                                    </div>
                                                                    <div className={`nav-item nav-link ${selectedPolicyTab === "Pairing" ? "active" : ""}`}
                                                                        onClick={() => handlePolicyTabClick("Pairing")} style={{ width: "140px" }} >
                                                                        Pairing
                                                                    </div>
                                                                    <div className={`nav-item nav-link ${selectedPolicyTab === "Clock Settings" ? "active" : ""}`}
                                                                        onClick={() => handlePolicyTabClick("Clock Settings")} style={{ width: "140px" }} >
                                                                        Clock
                                                                    </div>
                                                                    <div className={`nav-item nav-link ${selectedPolicyTab === "Carer Mode" ? "active" : ""}`}
                                                                        onClick={() => handlePolicyTabClick("Carer Mode")} style={{ width: "140px" }} >
                                                                        Carer Mode
                                                                    </div>
                                                                    <div className={`nav-item nav-link ${selectedPolicyTab === "Worker Check In Settings" ? "active" : ""}`}
                                                                        onClick={() => handlePolicyTabClick("Worker Check In Settings")} style={{ width: "145px" }} >
                                                                        Worker Check-in
                                                                    </div>
                                                                    <div className={`nav-item nav-link ${selectedPolicyTab === "Recording Settings" ? "active" : ""}`}
                                                                        onClick={() => handlePolicyTabClick("Recording Settings")} style={{ width: "165px" }} >
                                                                        Call Logs
                                                                    </div>
                                                                </div>
                                                            </nav>
                                                        </div>
                                                        <div className="col-md-9">
                                                            <div className="tab-content">
                                                                {selectedPolicyTab === "Pairing" && (
                                                                    <div>
                                                                        <PairingTab user={user} settingsFrom={settingsFrom} policy={selectedGroup} />
                                                                    </div>
                                                                )}
                                                                {selectedPolicyTab === "Do Not Disturb" && (
                                                                    <div>
                                                                        <DoNotDisturb user={user} settingsFrom={settingsFrom} policy={selectedGroup} />
                                                                    </div>
                                                                )}
                                                                {selectedPolicyTab === "Display" && (
                                                                    <div>
                                                                        <DisplaySettings user={user} settingsFrom={settingsFrom} policy={selectedGroup} />
                                                                    </div>
                                                                )}
                                                                {selectedPolicyTab === "Call Settings" && (
                                                                    <div>
                                                                        <SettingsTab onApplyToAllDevices={onApplyToAllDevices} onApplyToThisDevice={onApplyToThisDevice} settingsFrom={settingsFrom} policy={selectedGroup} />
                                                                    </div>
                                                                )}
                                                                {selectedPolicyTab === "Clock Settings" && (
                                                                    <div>
                                                                        <ClockSettingsTab user={user} settingsFrom={settingsFrom} policy={selectedGroup} />
                                                                    </div>
                                                                )}
                                                                {selectedPolicyTab === "Carer Mode" && (
                                                                    <div>
                                                                        <CarerMode user={user} settingsFrom={settingsFrom} policy={selectedGroup} />
                                                                    </div>
                                                                )}
                                                                {selectedPolicyTab === "Worker Check In Settings" && (
                                                                    <div>
                                                                        <WorkerCheckInTab user={user} settingsFrom={settingsFrom} policy={selectedGroup} />
                                                                    </div>
                                                                )}

                                                                {selectedPolicyTab === "Recording Settings" && (
                                                                    <div>
                                                                        <RecordingSettingsTab user={user} settingsFrom={settingsFrom} policy={selectedGroup} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                {selectedTab === "Devices in Group" && (
                                                    <div className="table-wrap padding_infor_info">
                                                        <Button onClick={handleAddDeviceClick} variant="action-popup">
                                                            + Add Device
                                                        </Button>
                                                        <Button onClick={handleGroupCallClick} variant="action-popup" style={{
                                                            marginLeft: "20px", marginBottom: "20px"
                                                        }}>
                                                            <PhoneCallIcon
                                                                sx={{
                                                                    fontSize: "1.2rem",

                                                                    marginRight: "5px",
                                                                    verticalAlign: "middle",
                                                                }}
                                                            />
                                                            Group Call
                                                        </Button>
                                                        <div className="form-group w-50">
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="Search devices..."
                                                                value={policyDeviceSearchQuery}
                                                                onChange={handlePolicyDeviceSearchInputChange}
                                                            />
                                                        </div>
                                                        <table>
                                                            <thead>
                                                                <tr>
                                                                    <th>CareWindow User</th>
                                                                    <th>Device S/N</th>
                                                                    <th>Date Enrolled</th>
                                                                    <th>Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {filteredPolicyDevices.map((device) => (
                                                                    <tr key={device.id}>
                                                                        <td>
                                                                            {device.profilePicture ? (
                                                                                <img
                                                                                    className="user-photo"
                                                                                    src={`data:image/png;base64, ${device.profilePicture}`}
                                                                                    alt={device.name}
                                                                                />
                                                                            ) : (
                                                                                <img
                                                                                    className="user-photo"
                                                                                    src={deviceProfile}
                                                                                    alt="Default profile"
                                                                                />
                                                                            )}
                                                                            {device.name}
                                                                        </td>
                                                                        <td style={{ verticalAlign: "middle" }}>
                                                                            {device.serialNo}
                                                                        </td>
                                                                        <td style={{ verticalAlign: "middle" }}>
                                                                            {new Date(
                                                                                device.orgEnrollDate
                                                                            ).toLocaleDateString("en-GB")}
                                                                        </td>
                                                                        <td style={{ verticalAlign: "middle" }}>
                                                                            <TrashIcon
                                                                                sx={{
                                                                                    fontSize: "1rem",
                                                                                    color: "black",
                                                                                    marginRight: "10px",
                                                                                    cursor: "pointer",
                                                                                }}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleRemoveClick(
                                                                                        device.id,
                                                                                        selectedGroup.id
                                                                                    );
                                                                                }}
                                                                            />
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="table-wrap padding_infor_info">
                                        <Button onClick={handleAddClick} variant="action-popup">
                                            + Add Device Group
                                        </Button>
                                        <div class="clear"></div>
                                        <br />
                                        <table className="table table-hover">
                                            <thead>
                                                <tr>
                                                    <th>Group Name</th>
                                                    <th>Device numbers</th>
                                                    <th>Last Edit</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredPolicyGroups.map((group) => (
                                                    <tr
                                                        key={group.id}
                                                        onClick={() => handleGroupClick(group)}
                                                    >
                                                        <td>{group.name}</td>
                                                        <td>{group.noOfDevices}</td>
                                                        <td>
                                                            {new Date(group.createdAt).toLocaleDateString(
                                                                "en-GB"
                                                            )}
                                                        </td>
                                                        <td>
                                                            <EditIcon
                                                                sx={{
                                                                    fontSize: "1rem",
                                                                    color: "black",
                                                                    marginRight: "10px",
                                                                    cursor: "pointer",
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEditClick(group);
                                                                }}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showPreviewModal && (
                <div
                    className="modal fade show"
                    style={{
                        display: "block",
                        backgroundColor: "rgba(0,0,0,0.5)",
                        overflowY: "auto", // 确保背景可滚动以防弹窗内容过高

                        paddingBottom: "20px",
                    }}
                    aria-modal="true"
                    role="dialog"
                >
                    <div
                        className="modal-dialog modal-lg modal-dialog-centered"
                        role="document"
                        style={{
                            maxWidth: "90%", // 根据需要调整宽度
                            margin: "auto",
                        }}
                    >
                        <div
                            className="modal-content"
                            style={{
                                backgroundColor: "#f0f0f0",
                                width: "100%",
                                padding: "15px",
                                maxHeight: "90vh", // 最大高度为视口高度的90%
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <div className="modal-header" style={{ flexShrink: 0 }}>
                                <h1 className="modal-title">Group Video Call Preview</h1>
                                <Button onClick={() => { setShowPreviewModal(false); cleanupPreviewTracks(); }} variant="modal-close">
                                    &times;
                                </Button>
                            </div>
                            <div
                                className="modal-body"
                                style={{
                                    padding: "15px",
                                    overflowY: "auto", // 允许内容滚动
                                    flexGrow: 1,
                                }}
                            >
                                <div
                                    style={{
                                        width: "100%",
                                        aspectRatio: "16/9",
                                        backgroundColor: "black",
                                        position: "relative",
                                        borderRadius: "8px",
                                        overflow: "hidden",
                                    }}
                                >
                                    <div
                                        ref={previewVideoRef}
                                        style={{ width: "100%", height: "100%" }}
                                    />
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        marginTop: "15px",
                                    }}
                                >
                                    <button
                                        onClick={togglePreviewMicrophone}
                                        style={buttonStyle(isPreviewMicrophoneEnabled)}
                                    >
                                        {isPreviewMicrophoneEnabled ? (
                                            <KeyboardVoiceRounded />
                                        ) : (
                                            <MicOffRounded />
                                        )}
                                    </button>
                                    <button
                                        onClick={togglePreviewCamera}
                                        style={buttonStyle(isPreviewCameraEnabled)}
                                    >
                                        {isPreviewCameraEnabled ? (
                                            <VideocamRounded />
                                        ) : (
                                            <VideocamOffRounded />
                                        )}
                                    </button>
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        marginTop: "15px",
                                    }}
                                >
                                    {/* Microphone Selection */}
                                    <div
                                        style={{
                                            width: "100%",
                                            maxWidth: "350px",
                                            marginBottom: "10px",
                                        }}
                                    >
                                        <label
                                            htmlFor="audio-input-select"
                                            style={{
                                                display: "block",
                                                marginBottom: "5px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Microphone
                                        </label>
                                        <select
                                            id="audio-input-select"
                                            value={selectedAudioInput}
                                            onChange={(e) =>
                                                handleDeviceChange(e, setSelectedAudioInput)
                                            }
                                            style={{
                                                width: "100%",
                                                padding: "8px",
                                                borderRadius: "5px",
                                                border: "1px solid #ccc",
                                                fontSize: "14px",
                                            }}
                                        >
                                            <option value="">
                                                System Default -{" "}
                                                {audioInputDevices[0]?.label || "No Device"}
                                            </option>
                                            {audioInputDevices.map((device) => (
                                                <option key={device.deviceId} value={device.deviceId}>
                                                    {device.label || "Unnamed Audio Input"}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {/* Speaker Selection */}
                                    <div
                                        style={{
                                            width: "100%",
                                            maxWidth: "350px",
                                            marginBottom: "10px",
                                        }}
                                    >
                                        <label
                                            htmlFor="audio-output-select"
                                            style={{
                                                display: "block",
                                                marginBottom: "5px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Speaker
                                        </label>
                                        <select
                                            id="audio-output-select"
                                            value={selectedAudioOutput}
                                            onChange={(e) =>
                                                handleDeviceChange(e, setSelectedAudioOutput)
                                            }
                                            style={{
                                                width: "100%",
                                                padding: "8px",
                                                borderRadius: "5px",
                                                border: "1px solid #ccc",
                                                fontSize: "14px",
                                            }}
                                        >
                                            <option value="">
                                                System Default -{" "}
                                                {audioOutputDevices[0]?.label || "No Device"}
                                            </option>
                                            {audioOutputDevices.map((device) => (
                                                <option key={device.deviceId} value={device.deviceId}>
                                                    {device.label || "Unnamed Audio Output"}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {/* Camera Selection */}
                                    <div style={{ width: "100%", maxWidth: "350px" }}>
                                        <label
                                            htmlFor="camera-select"
                                            style={{
                                                display: "block",
                                                marginBottom: "5px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Camera
                                        </label>
                                        <select
                                            id="camera-select"
                                            value={selectedCamera}
                                            onChange={(e) => handleDeviceChange(e, setSelectedCamera)}
                                            style={{
                                                width: "100%",
                                                padding: "8px",
                                                borderRadius: "5px",
                                                border: "1px solid #ccc",
                                                fontSize: "14px",
                                            }}
                                        >
                                            <option value="">
                                                System Default - {videoDevices[0]?.label || "No Device"}
                                            </option>
                                            {videoDevices.map((device) => (
                                                <option key={device.deviceId} value={device.deviceId}>
                                                    {device.label || "Unnamed Camera"}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div
                                className="modal-footer"
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    flexShrink: 0,
                                }}
                            >
                                <Button onClick={() => { setShowPreviewModal(false); cleanupPreviewTracks(); }} variant="cancel">
                                    Cancel
                                </Button>
                                <Button onClick={handleStartGroupCall}>
                                    Start Call
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showAddPolicyGroupModal && (
                <div className="modal" tabIndex={"-1"} role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h1 className="modal-title">Add Device Group</h1>
                                <Button onClick={handleAddCancel} variant="modal-close">
                                    &times;
                                </Button>
                            </div>
                            <div className="modal-body">
                                <form className="contact-form">
                                    <div className="form-group">
                                        <label htmlFor="name">Name</label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={newPolicyGroup.name}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="modal-footer">
                                        <Button onClick={handleAddCancel} variant="cancel-light">
                                            Cancel
                                        </Button>
                                        <Button onClick={handleAddSubmit} variant="black">
                                            Add
                                        </Button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteModal && (
                <div
                    className="modal fade show"
                    style={{ display: "block" }}
                    aria-modal="true"
                    role="dialog"
                >
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h1 className="modal-title">Delete Policy Group</h1>
                                <Button onClick={handleDeleteGroupCancel} variant="modal-close">
                                    &times;
                                </Button>
                            </div>
                            <div className="modal-body">
                                <p>
                                    Are you sure you want to delete{" "}
                                    <strong>
                                        {
                                            policyGroups.find((group) => group.id === deleteGroupId)
                                                ?.name
                                        }
                                    </strong>
                                    ?
                                </p>
                            </div>
                            <div className="modal-footer">
                                <Button onClick={handleDeleteGroupCancel} variant="cancel">
                                    Cancel
                                </Button>
                                <Button onClick={handleDeleteGroupConfirm} variant="delete">
                                    Delete
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showAddDeviceModal && (
                <div className="modal" tabIndex={"-1"} role="dialog">
                    <div className="modal-dialog" role="document">
                        <div
                            className="modal-content"
                            style={{ height: "98vh", minWidth: "700px" }}
                        >
                            <div className="modal-header">
                                <h1 className="modal-title">Add Devices to Policy Group</h1>
                                <Button onClick={() => setShowAddDeviceModal(false)} variant="modal-close">
                                    &times;
                                </Button>
                            </div>
                            <div className="modal-body">
                                <div className="form-group">
                                    <input
                                        type="text"
                                        className="form-control w-50"
                                        placeholder="Search devices..."
                                        value={searchQuery}
                                        onChange={handleSearchInputChange}
                                    />
                                </div>
                                <PaginatedDeviceSelectionTable
                                    devices={filteredDevices}
                                    selectedDevices={selectedDevices}
                                    onDeviceSelect={handleDeviceSelect}
                                    itemsPerPage={8}
                                />
                            </div>
                            <div className="modal-footer">
                                <Button onClick={() => setShowAddDeviceModal(false)} variant="cancel">
                                    Cancel
                                </Button>
                                <Button onClick={handleAddDevicesToGroup} disabled={selectedDevices.length === 0}>
                                    Add Devices
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showEditPolicyGroupModal && (
                <div className="modal" tabIndex={"-1"} role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h1 className="modal-title">Edit Policy Group</h1>
                                <Button onClick={handleEditCancel} variant="modal-close">
                                    &times;
                                </Button>
                            </div>
                            <div className="modal-body">
                                <form className="contact-form">
                                    <div className="form-group">
                                        <label htmlFor="name">Name</label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={editedPolicyGroup.name}
                                            onChange={handleEditInputChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="status">Status</label>
                                        <select
                                            className="form-control"
                                            id="status"
                                            name="status"
                                            value={editedPolicyGroup.status}
                                            onChange={handleEditInputChange}
                                        >
                                            <option value="Active">Active</option>
                                            <option value="Inactive">Inactive</option>
                                        </select>
                                    </div>
                                    {editedPolicyGroup.status === "Inactive" && (
                                        <div className="alert alert-warning" role="alert">
                                            Are you sure you want to set the "{editedPolicyGroup.name}
                                            " as inactive? This action will release{" "}
                                            {editedPolicyGroup.noOfDevices} devices out of the policy
                                            group and delete this policy group.
                                        </div>
                                    )}
                                    <div className="modal-footer">
                                        <Button onClick={handleEditCancel} variant="cancel-light">
                                            Cancel
                                        </Button>
                                        <Button onClick={handleEditSubmit} variant="black">
                                            Save
                                        </Button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PolicyGroups;
